import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ApolloClient } from '@apollo/client';
import { Observable, filter, take } from 'rxjs';
import { MicroApp } from '../class/micro-app.class';
import { Config } from '../model/config.model';
import { HostApi } from '../model/host-api';
import { MicroFrontendLoadedDetail } from '../model/micro-frontend-loaded-detail';
import { TempRoutingOptions } from './appHost.service';

@Injectable({
  providedIn: 'root',
})
export class HostApiService implements HostApi {
  constructor(@Inject(DOCUMENT) document: Document) {
    /**
     * Dispatch an event to RCP to initialize this API instance (as you'll notice, all methods and props are stubbed-out
     * which makes it unusuable until initialized by RCP).
     *
     * NOTE: Manually triggered events are synchronous, so this RCP API instance will be initialized immediately
     * after the event is dispatched.
     */
    const event = new CustomEvent('finraMicroAppLoaded', { detail: { service: this } as MicroFrontendLoadedDetail });
    (document.querySelector('finra-app-host') as HTMLElement)?.dispatchEvent(event) ||
      console.warn('Unable to initialize RCP Host API because micro-frontend is not running in an RCP context');
  }

  public activeMicroApp: MicroApp;
  public packagePath: string;
  public params;
  public initialized;
  public assetPath;
  public active: Element;
  public config: Config;

  public routing: {
    navigate: (url: string, options?: TempRoutingOptions) => void;
    routes: Observable<any>;
    events: Observable<any>;
    registry: Observable<any>;
    active: Observable<any>;
  };

  public user: {
    info: Observable<{
      crdFirmId?: string | number;
      firmId?: string;
      firmName?: string;
      firstName?: string;
      lastName?: string;
      userName?: string;
      userId?: string;
    }>;
    profile: Observable<{
      tmp: string;
    }>;
    preferences: Observable<{
      startingRoute: string;
      language: string;
    }>;
    setLanguage: (lang) => void;
  };

  public session: {
    stop: () => void;
    logout: () => void;
  };

  public error: {
    failedToLoad: Observable<any>;
  };

  public info: {
    data: Observable<any>;
  };

  public package: {
    package: Observable<any>;
    registry: Observable<any>;
  };

  public locking: {
    data: Observable<any>;
  };

  public modal: {
    openAngularComponent: (component: string, componentParams: any, modalParam: any, layer: string) => number;
    openWebComponent: (component: string, componentParams: any, modalParam: any, layer: string) => number;
    close: (modalReferenceId: number, layer: string) => void;
  };

  public popup: {
    openPopup: (component, componentParam, popupParams) => void;
    expand: (index) => void;
  };

  public logging: {
    alert: (level, message, source) => void;
    log: (level, message, source) => void;
    critical: (
      ags: string,
      component: string,
      message: string,
      sessionId?: string,
      userId?: string,
      correlationId?: string,
    ) => Promise<void>;
    error: (
      ags: string,
      component: string,
      message: string,
      sessionId?: string,
      userId?: string,
      correlationId?: string,
    ) => Promise<void>;
    warn: (
      ags: string,
      component: string,
      message: string,
      sessionId?: string,
      userId?: string,
      correlationId?: string,
    ) => Promise<void>;
    info: (
      ags: string,
      component: string,
      message: string,
      sessionId?: string,
      userId?: string,
      correlationId?: string,
    ) => Promise<void>;
  };

  public notification: {};

  public view: {
    state: Observable<any>;
    setPageTitle: (title: string, source: string, options?: { label?: string }) => void;
  };

  public component: {
    load: (microAppId: string, params: any, targetView: HTMLElement, options?: any) => Promise<HTMLElement>;
  };

  public events: {
    post: (request: {
      correlationId?: string /** CorrelationId: null */;
      ags: string /** ?: DXTGATEW */;
      component: string /** Component Name: app-shell */;
      action: string /** Action: view */;
      resource: string /** Resource: app-shell */;
      details: any /** Details: {} */;
    }) => Promise<any>;
    data: Observable<any[]>;
    sessionId: Observable<any>;
    test: () => void;
  };

  public date: {
    toUser: (date: Date, format: string, timeZone: string) => Promise<string>;
    toServer: (date: string) => string;
  };

  public entitlements: {
    permissions: Observable<string[]>;
    userType: Observable<string>;
    isEnabled: (access: string) => Observable<string | boolean>;
    isAllEnabled: (access: string[]) => Observable<boolean>;
    classification: Observable<string>;
    isClassificationEnabled: (access: string) => Observable<boolean>;
  };

  public features: {
    features: Observable<unknown>;
    isEnabled: (feature: string) => Observable<string | boolean>;
  };

  public toast: {
    open: (param: { theme: string; text: string; fadeFast?: boolean }) => HTMLElement;
  };

  public firmService: {
    setSelectedFirm: (firmId: any, firmName: any, firmCity: any, expires?: any) => void;
    getSelectedFirm: () => { firmId: string; firmName: string; firmCity: string };
    fetchSelectedFirm: () => Observable<{ firmId: string; firmName: string; firmCity: string }>;
  };

  public gql: {
    getClient: (uri, credentials) => ApolloClient<any>;
    query: (param: any) => any;
    mutate: (param: any) => any;
    setClient: any;
    watchQuery: any;
  };

  public navigation: {
    showUnsavedChangesDialog: (useUnsavedTheme?: boolean) => Promise<string | boolean>;
  };

  // async next() {
  //   await this.initialized.pipe(
  //     filter(value => !!value),
  //     take(1),
  //   ).toPromise();
  //   return this;
  // }

  navigate(commands: string | any[], options?: any) {
    this.config.pipe(take(1)).subscribe((config) => {
      let rootPath = config.basePath;
      if (options) {
        if (options.fromAppHost || options.root) {
          rootPath = '';
        } else if (options.relative) {
          rootPath = config.basePath;
        }
      }

      // redirect starting from the component base url
      if (typeof commands === 'string') {
        this.routing.navigate(`${rootPath}${commands}`, options);
      } else {
        this.routing.navigate(`${rootPath}/${commands.join('/')}`, options);
      }
    });
  }
}
